import { createContext, ReactNode, useContext, useMemo } from 'react';
import { ViewManager } from '~/engine/view/ViewManager';

export interface AppServices {
    readonly engine: ViewManager;
}

const AppServicesContext = createContext<AppServices | null>(null);

interface Props {
    /**
     * The live engine instance, or null before the first viewport mounts.
     * The provider is rendered unconditionally (so the React tree shape
     * never changes when the engine initializes); the context value flips
     * null → services instead.
     */
    engine: ViewManager | null;
    children: ReactNode;
}

export function AppServicesProvider({ engine, children }: Props) {
    const value = useMemo<AppServices | null>(
        () => (engine ? { engine } : null),
        [engine]
    );
    return (
        <AppServicesContext.Provider value={value}>
            {children}
        </AppServicesContext.Provider>
    );
}

export function useAppServices(): AppServices {
    const ctx = useContext(AppServicesContext);
    if (!ctx) {
        throw new Error(
            'useAppServices must be used inside AppServicesProvider ' +
                'after the engine has initialized'
        );
    }
    return ctx;
}

/** Null until the engine initializes; for consumers that must mount earlier. */
export function useAppServicesOrNull(): AppServices | null {
    return useContext(AppServicesContext);
}
